/* eslint-disable max-lines-per-function */
import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import styles from './TimerStyles.module.css';
import { calculateDateTime } from './util';
export interface NativeColorPicker {
  color: string;
  plugin: string;
  _uid: string;
}
export interface TimerSchema {
  startTime: string;
  endTime: string;
  labelPlacement?: 'top' | 'bottom';
  promoLabel?: string;
  timerColor?: string;
  backgroundColor: string;
  dayLabel: string;
  hourLabel: string;
  minuteLabel: string;
  secondLabel: string;
  mainHeading: string;
  enableCounter: boolean;
}
interface StaticLineTabelTimer {
  content: TimerSchema;
  hasHighlightText?: boolean;
  onTimerChange?: (active: boolean) => void;
}
export interface timeleft {
  days: string;
  hours: string;
  mins: string;
  sec: string;
}
const StaticLineTabelTimer = ({
  content,
  hasHighlightText = false,
  onTimerChange
}: StaticLineTabelTimer): JSX.Element | null => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCounterEnabled = content?.enableCounter;

  const expiry = useMemo(
    () => calculateDateTime(new Date(content?.endTime?.replace(/-/g, '/'))),
    [content?.endTime]
  );
  const start = useMemo(
    () => calculateDateTime(new Date(content?.startTime?.replace(/-/g, '/'))),
    [content?.startTime]
  );

  const hasStartTime = Boolean(content?.startTime?.trim());
  const hasValidEndTime = !Number.isNaN(expiry.getTime());
  const isEndTimePassed = hasValidEndTime && new Date().getTime() >= expiry.getTime();
  const [startTimer, setStartTimer] = useState(
    isCounterEnabled &&
      !isEndTimePassed &&
      (!hasStartTime || new Date().getTime() >= start.getTime())
  );

  const calculateTime = (): timeleft | null => {
    if (!startTimer) {
      return null;
    }

    const today = new Date();
    const difference = expiry.getTime() - today.getTime();

    if (difference > 0) {
      const days = ('0' + Math.floor(difference / (1000 * 60 * 60 * 24))).slice(-2); // adding leading zeroes for single digit
      const hours = ('0' + Math.floor((difference / (1000 * 60 * 60)) % 24)).slice(-2);
      const mins = ('0' + Math.floor((difference / 1000 / 60) % 60)).slice(-2);
      const sec = ('0' + Math.floor((difference / 1000) % 60)).slice(-2);
      return { days, hours, mins, sec };
    }
    return null;
  };
  const [timeLeft, setTimeLeft] = useState<timeleft | null>(null);

  useEffect(() => {
    onTimerChange?.(isCounterEnabled ? startTimer : false);
  }, [startTimer, isCounterEnabled]);

  useEffect(() => {
    if (!isCounterEnabled) {
      setStartTimer(false);
      return;
    }

    const now = Date.now();
    const endMs = expiry.getTime();
    const startMs = start.getTime();
    const hasValidEndTime = !Number.isNaN(endMs);
    const hasValidStartTime = !Number.isNaN(startMs);

    if (!hasValidEndTime || now >= endMs) {
      setStartTimer(false);
      return;
    }

    if (!hasStartTime) {
      setStartTimer(true);
      return;
    }

    if (!hasValidStartTime) {
      setStartTimer(false);
      return;
    }

    const shouldStartNow = now >= startMs;
    setStartTimer(shouldStartNow);

    if (shouldStartNow) {
      return;
    }

    const delay = startMs - now;
    const timeout = setTimeout(() => {
      setStartTimer(true);
    }, delay);

    return () => {
      clearTimeout(timeout);
    };
  }, [isCounterEnabled, hasStartTime, start, expiry]);

  useEffect(() => {
    if (!isCounterEnabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setTimeLeft(null);
      return;
    }

    if (!startTimer) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setTimeLeft(null);
      return;
    }

    const expiryMs = expiry.getTime();
    if (Number.isNaN(expiryMs) || Date.now() >= expiryMs) {
      setStartTimer(false);
      setTimeLeft(null);
      return;
    }

    setTimeLeft(calculateTime());
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const timer = setInterval(() => {
      if (Date.now() >= expiryMs) {
        setStartTimer(false);
        setTimeLeft(null);
        clearInterval(timer);
        intervalRef.current = null;
        return;
      }
      setTimeLeft(calculateTime());
    }, 1000);
    intervalRef.current = timer;

    return () => {
      clearInterval(timer);
      if (intervalRef.current === timer) {
        intervalRef.current = null;
      }
    };
  }, [isCounterEnabled, startTimer, content?.endTime]);
  const timeLeftArray = [
    //adding hours, mins and seconds to array so that this array can be mapped for displaying values
    { label: content?.hourLabel ?? '', value: timeLeft?.hours },
    { label: content?.minuteLabel ?? '', value: timeLeft?.mins },
    { label: content?.secondLabel ?? '', value: timeLeft?.sec }
  ];
  const isLabelBottom = content?.labelPlacement === 'bottom';
  const hasLabels = Boolean(
    content?.dayLabel || content?.hourLabel || content?.minuteLabel || content?.secondLabel
  );

  const renderTimerElement = (label: string, value?: string) => {
    return (
      <>
        {!isLabelBottom ? <div className={styles.timerLabel}>{label}</div> : null}
        <div className={styles.timerValue}> {value}</div>
        {isLabelBottom ? <div className={styles.timerLabel}>{label}</div> : null}
      </>
    );
  };

  return isCounterEnabled && startTimer && timeLeft ? (
    <div
      className={`${styles.timerWrap} ${hasHighlightText ? styles.timerWrapHighlight : ''}`}
      style={{
        color: content?.timerColor ?? '',
        backgroundColor: content?.backgroundColor ?? 'transparent'
      }}
    >
      <div className={styles.timerContainer}>
        <div className={styles.timerSection}>
          <div className={`${styles.timerElement} ${styles.transparentElement}`}>
            {renderTimerElement(content.dayLabel, timeLeft?.days)}
          </div>
          {timeLeftArray.map((element: any, index: number) => (
            <React.Fragment key={index}>
              <div
                className={`${styles.colon} ${!isLabelBottom && hasLabels ? styles.colonTop : ''}`}
              >
                :
              </div>
              <div className={styles.timerElement}>
                {renderTimerElement(element.label, element.value)}
              </div>
            </React.Fragment>
          ))}
        </div>
        {content?.promoLabel ? (
          <div className={styles.timerMessage}>{content.promoLabel}</div>
        ) : null}
      </div>
    </div>
  ) : null;
};

export default StaticLineTabelTimer;
